import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/auth/AuthShell";

const hashEmail = async (email: string): Promise<string> => {
  const data = new TextEncoder().encode(email.toLowerCase() + "rate_limit_salt");
  const hash = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, "0")).join("");
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Enter your email");
      return;
    }
    setLoading(true);
    try {
      const emailHash = await hashEmail(email.trim());
      const { data: rl, error: rlErr } = await supabase.rpc("check_rate_limit_enhanced", {
        p_action_type: "password_reset",
        p_email_hash: emailHash,
        p_max_actions: 3,
        p_window_minutes: 15,
      });
      if (!rlErr && rl) {
        const r = rl as { allowed: boolean; retry_after: number };
        if (!r.allowed) {
          toast.error("Too many requests", {
            description: `Wait ${Math.ceil((r.retry_after || 900) / 60)} minutes before trying again.`,
          });
          setLoading(false);
          return;
        }
      }
      const { error } = await supabase.functions.invoke("send-reset-email", {
        body: { email: email.trim(), redirectTo: `${window.location.origin}/reset-password` },
      });
      if (error) throw error;
      setSent(true);
      toast.success("Reset email sent");
    } catch (err: any) {
      toast.error("Couldn't send reset email", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Forgot password | EchoCRM</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <AuthShell
        eyebrow={sent ? "Check your inbox" : "Reset password"}
        title={sent ? "Email on the way." : "Forgot your password?"}
        subtitle={
          sent
            ? "We've sent reset instructions. Open the link from your email to continue."
            : "Enter your email and we'll send you a reset link."
        }
        footer={
          <Link to="/login" className="inline-flex items-center gap-1.5 hover:text-foreground">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </Link>
        }
      >
        {sent ? (
          <div className="space-y-5">
            <div className="flex items-center gap-3 px-4 py-3 rounded-md border border-border bg-secondary">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-[14px]">{email}</span>
            </div>
            <p className="text-[13px] text-muted-foreground">
              Didn't receive it? Check your spam folder, or{" "}
              <button onClick={() => setSent(false)} className="text-foreground hover:text-primary">
                try again
              </button>.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[13px] font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button type="submit" size="lg" className="w-full" disabled={loading}>
              {loading ? "Sending…" : <>Send reset link <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>
        )}
      </AuthShell>
    </>
  );
};

export default ForgotPassword;